import { Component, OnInit, inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PanelComponent } from '@rocksoft/cdk';
import { AppleApplication, VppAsset } from '@rocksoft/mdm-sdk-v2';
import { ErrorModel } from '@rocksoft/shared';
import { Observable, Subject, merge } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { AppleApplicationListContainerComponent } from '../../../shared/components/application-list/application-list.container';
import { VppAssetListContainerComponent } from '../../../shared/components/vpp-asset-list/vpp-asset-list.container';
import { AppleCommandProgressService } from '../../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../../shared/services/apple-mdm-service';
import { CommandSpinnerComponent } from '../../../utilities/command-spinner/command-spinner.component';
import { UploadSpinnerComponent } from '../../../utilities/upload-spinner/upload-spinner.component';
import { PackageType } from '../../../viewmodels/apple/apple-constants.model';
import { UploadProgress } from '../../../viewmodels/apple/apple-upload-progress.model';
import { RequestType } from '../../../viewmodels/apple/commands/apple-command.model';
import { CommandProgress } from '../../../viewmodels/apple/signalr/command-context.model';

enum ApplicationKind {
  Manual = 'Manual',
  Vpp = 'Vpp',
  Default = 'Default',
}
@Component({
  selector: 'ml-install-application-modal',
  templateUrl: './install-application-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    UploadSpinnerComponent,
    CommandSpinnerComponent,
    PanelComponent,
    AppleApplicationListContainerComponent,
    VppAssetListContainerComponent,
    ReactiveFormsModule,
    FormsModule,
    FaIconComponent,
  ],
})
export class InstallApplicationModalComponent implements OnInit {
  private readonly commandProgressService = inject(AppleCommandProgressService);
  private readonly mdmService = inject(AppleMdmService);
  public readonly modal = inject(NgbActiveModal);

  readonly applicationList = viewChild.required<AppleApplicationListContainerComponent>('applicationList');
  readonly vppAssetList = viewChild.required<VppAssetListContainerComponent>('vppAssetList');

  public readonly udid: string;

  private readonly _commandProgress = new Subject<CommandProgress>();
  public commandProgress$: Observable<CommandProgress>;

  public package: File | undefined;
  public error: ErrorModel;
  public appKind = ApplicationKind.Default;
  public allAppKinds = ApplicationKind;

  public uploadProgress: UploadProgress = {
    isLoading: false,
    progress: 0,
  };

  private selectedApplication: AppleApplication | VppAsset;

  public ngOnInit(): void {
    this.commandProgress$ = merge(
      this._commandProgress,
      this.commandProgressService.commandsOfRequestType(this.udid, 'InstallApplication')
    );
  }
  // Note: It makes a directory on the server-side client with the name of the selected type.
  public packageTypes(): string[] {
    return Object.keys(PackageType);
  }

  public onSubmit(pck: File | undefined): void {
    if (this.appKind === ApplicationKind.Manual) {
      if (!pck) {
        return;
      }

      const command = this.createInitialCommandProgress(this.udid, 'InstallApplication', 'Sending');
      this._commandProgress.next(command);

      this.uploadProgress.isLoading = true;
      this.mdmService.installManualApp(pck, this.udid).subscribe((app) => {
        if (!(typeof app === 'number')) {
          if (!app) {
            return;
          }
          this.uploadProgress.isLoading = false;
          this.uploadProgress.progress = 0;
          return;
        }

        this.uploadProgress.progress = app;
      });
    } else if (this.appKind === ApplicationKind.Vpp) {
      const application = this.selectedApplication as VppAsset;
      if (!application) {
        return;
      }

      const command = this.createInitialCommandProgress(this.udid, 'InstallApplication', 'Sending');
      this._commandProgress.next(command);

      this.mdmService
        .installVppApplication(this.udid, application.adamIdStr)
        .pipe(
          concatMap(() => this.mdmService.getApps(this.udid)),
          concatMap(() => this.mdmService.getManagedApplications(this.udid))
        )
        .subscribe();
    } else {
      const application = this.selectedApplication as AppleApplication;
      if (!application) {
        return;
      }

      const command = this.createInitialCommandProgress(this.udid, 'InstallApplication', 'Sending');
      this._commandProgress.next(command);

      this.mdmService
        .installKnownApp(this.udid, application.bundleIdentifier, application.bundleVersion)
        .pipe(
          concatMap(() => this.mdmService.getApps(this.udid)),
          concatMap(() => this.mdmService.getManagedApplications(this.udid))
        )
        .subscribe();
    }
  }

  private createInitialCommandProgress(udid: string, requestType: RequestType, status: string): CommandProgress {
    return {
      udid,
      commandUUID: '',
      status,
      requestType,
    };
  }

  public selectApplicationChanged(selection: AppleApplication | VppAsset) {
    this.selectedApplication = selection;
  }
}
